import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const DEFAULT_REPORT_TIMEZONE_OFFSET = 7;
export const DEFAULT_REPORT_DAY_OFFSET = 1;

export const REPORT_DAY_OFFSET_OPTIONS = Array.from(
  { length: 8 },
  (_, value) => ({
    value,
    label: `H-${value}`,
  }),
);

const SUPPORTED_REPORT_TIMEZONES = [7, 8, 9];

export interface ReportScheduleMetadata {
  sendingAt?: Date | string;
  timeFrom?: string;
  timeTo?: string;
  timezoneOffset?: number;
  timeFromDayOffset?: number;
  timeToDayOffset?: number;
  startAt?: number;
  endAt?: number;
}

interface ReportScheduleFormValues {
  sendingAt?: Dayjs;
  timeFrom?: Dayjs;
  timeTo?: Dayjs;
  timeFromDayOffset?: number;
  timeToDayOffset?: number;
}

export const getReportTimezoneOffset = (value?: number): number =>
  value !== undefined && SUPPORTED_REPORT_TIMEZONES.includes(value)
    ? value
    : DEFAULT_REPORT_TIMEZONE_OFFSET;

export const toSendingAtIso = (
  sendingAt: Dayjs,
  timezoneOffset: number,
): string =>
  dayjs()
    .utcOffset(getReportTimezoneOffset(timezoneOffset) * 60)
    .set('hour', sendingAt.hour())
    .set('minute', sendingAt.minute())
    .set('second', 0)
    .set('millisecond', 0)
    .toISOString();

export const formatReportSendingTime = (
  sendingAt: Date | string,
  timezoneOffset?: number,
): string =>
  dayjs(sendingAt)
    .utcOffset(getReportTimezoneOffset(timezoneOffset) * 60)
    .format('HH:mm');

const getComparableMinute = (dayOffset: number, time: Dayjs): number =>
  -dayOffset * 24 * 60 + time.hour() * 60 + time.minute();

export const getScheduleWindowValidationError = (
  values: ReportScheduleFormValues,
): string | undefined => {
  if (!values.sendingAt || !values.timeFrom || !values.timeTo) {
    return undefined;
  }

  const fromMinute = getComparableMinute(
    values.timeFromDayOffset ?? DEFAULT_REPORT_DAY_OFFSET,
    values.timeFrom,
  );
  const toMinute = getComparableMinute(
    values.timeToDayOffset ?? DEFAULT_REPORT_DAY_OFFSET,
    values.timeTo,
  );
  const sendingMinute =
    values.sendingAt.hour() * 60 + values.sendingAt.minute();

  if (toMinute > sendingMinute) {
    return 'Data Time To cannot be later than Sending Time';
  }

  if (fromMinute >= toMinute) {
    return 'Data Time From must be earlier than Data Time To';
  }

  return undefined;
};

const parseTime = (value: string): { hour: number; minute: number } => {
  const [hour = 0, minute = 0] = value.split(':').map(Number);
  return { hour, minute };
};

export const getEmailLogScheduleRange = (
  metaTarget: ReportScheduleMetadata,
  sendingScheduleDate: Date | string,
): { startAt: number; endAt: number } => {
  const storedStartAt = Number(metaTarget.startAt);
  const storedEndAt = Number(metaTarget.endAt);

  if (
    Number.isFinite(storedStartAt) &&
    Number.isFinite(storedEndAt) &&
    storedStartAt > 0 &&
    storedEndAt > 0
  ) {
    return { startAt: storedStartAt, endAt: storedEndAt };
  }

  const timezoneOffset = getReportTimezoneOffset(metaTarget.timezoneOffset);
  const fromDayOffset =
    metaTarget.timeFromDayOffset ?? DEFAULT_REPORT_DAY_OFFSET;
  const toDayOffset = metaTarget.timeToDayOffset ?? DEFAULT_REPORT_DAY_OFFSET;
  const localScheduleDate = dayjs(sendingScheduleDate).utcOffset(
    timezoneOffset * 60,
  );
  const scheduledTime = dayjs(metaTarget.sendingAt || sendingScheduleDate)
    .utcOffset(timezoneOffset * 60)
    .second(0);
  const fromTime = parseTime(metaTarget.timeFrom || '00:00:00');
  const toTime = parseTime(metaTarget.timeTo || '00:00:00');
  const scheduledAt = localScheduleDate
    .set('hour', scheduledTime.hour())
    .set('minute', scheduledTime.minute())
    .set('second', 0)
    .set('millisecond', 0);
  const start = scheduledAt
    .subtract(fromDayOffset, 'day')
    .set('hour', fromTime.hour)
    .set('minute', fromTime.minute)
    .set('second', 0);
  const endMinute = scheduledAt
    .subtract(toDayOffset, 'day')
    .set('hour', toTime.hour)
    .set('minute', toTime.minute)
    .set('second', 0);

  return {
    startAt: start.unix(),
    endAt: endMinute.isSame(scheduledAt)
      ? endMinute.unix()
      : endMinute.set('second', 59).unix(),
  };
};
